/**
 * Header scroll behaviour
 * - Hides the header when scrolling down, reveals it when scrolling up.
 * - Adds a `header--scrolled` separation state once the page is scrolled.
 * - Always visible at the top of the page.
 *
 * Mirrors the rAF + ticking pattern used by scroll-to-top.js.
 */
export default (function () {
    const header = document.querySelector('.header');
    if (!header) return;

    const TOP_THRESHOLD = 10;   // px — treated as "at the top"
    const HIDE_AFTER = 80;      // px — don't hide until scrolled past the header
    const DELTA = 6;            // px — ignore sub-threshold scroll noise (anti-jitter)

    let lastScrollY = window.scrollY;
    let ticking = false;
    let isScrolled = false;
    let isHidden = false;

    const setScrolled = (nextState) => {
        if (isScrolled === nextState) return;

        isScrolled = nextState;
        header.classList.toggle('header--scrolled', nextState);
    };

    const setHidden = (nextState) => {
        if (isHidden === nextState) return;

        isHidden = nextState;
        header.classList.toggle('header--hidden', nextState);
    };

    const update = () => {
        const currentY = window.scrollY;

        if (currentY <= TOP_THRESHOLD) {
            // At the top, restore the natural header position without a transform animation.
            header.classList.add('header--at-top');
            setScrolled(false);
            setHidden(false);
            window.requestAnimationFrame(() => {
                header.classList.remove('header--at-top');
            });
            lastScrollY = currentY;
            ticking = false;
            return;
        }

        setScrolled(true);

        // Only react to meaningful movement so tiny jumps don't toggle state
        if (Math.abs(currentY - lastScrollY) > DELTA) {
            if (currentY > lastScrollY && currentY > HIDE_AFTER) {
                setHidden(true);
            } else if (currentY < lastScrollY) {
                setHidden(false);
            }
            lastScrollY = currentY;
        }

        ticking = false;
    };

    window.addEventListener('scroll', () => {
        if (!ticking) {
            window.requestAnimationFrame(update);
            ticking = true;
        }
    }, { passive: true });

    // Initialise state on load
    update();
})();
